// The Lobby's « Social » tab: the invitations received, and the players met recently — online or not —,
// each one a tap away from their card, and the online ones from an invitation.
import React, { useState } from "react";
import { Check, Loader2, Mail, Send, Users } from "lucide-react";
import { InviteCard } from "../players/InviteCard";
import { PlayerCardDialog } from "../players/PlayerCardDialog";
import { useSendInvite } from "../players/invites";
import { Recent } from "../rank/RankViews";
import { t } from "../../lib/i18n";

function Section({ icon: Icon, title, count, children, testId }) {
  return (
    <section className="rounded-2xl border border-white/[0.07] bg-slate-900/80 p-3 shadow-[0_6px_24px_rgba(0,0,0,0.3)]" data-testid={testId}>
      <h3 className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-amber-300/90">
        <Icon className="h-4 w-4" />
        {title}
        {count > 0 && <span className="rounded-full bg-rose-500 px-1.5 text-[10px] leading-4 text-white">{count}</span>}
      </h3>
      {children}
    </section>
  );
}

export function SocialTab({ invites, onAnswerInvite, inviteActing, inviteErrors, busy }) {
  const [cardOf, setCardOf] = useState(null);
  const { send, status } = useSendInvite();

  // At the end of a recent player's row: « Inviter » while they are in the app (not during their game).
  const inviteButton = (p) => {
    if (p.status === "in_game") return <span className="w-[74px] shrink-0 text-center text-[10px] text-amber-300">{t("players.status.in_game")}</span>;
    const state = status(p.user_id);
    const failed = state && state !== "sending" && state !== "sent";
    return (
      <button
        type="button"
        onClick={() => send(p.user_id)}
        disabled={busy || state === "sending" || state === "sent"}
        title={failed ? state : busy ? t("players.finishGame") : undefined}
        data-testid={`social-invite-${p.user_id}`}
        className={`flex h-7 w-[72px] shrink-0 items-center justify-center gap-1 rounded-full text-[11px] transition-colors disabled:opacity-50 ${
          state === "sent" ? "bg-emerald-500/15 text-emerald-300" : failed ? "bg-rose-500/15 text-rose-300" : "bg-amber-400 text-slate-950 hover:bg-amber-300"
        }`}
      >
        {state === "sending" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : state === "sent" ? <Check className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5 rtl:-scale-x-100" />}
        {state === "sent" ? t("social.sent") : t("social.invite")}
      </button>
    );
  };

  return (
    <div className="mx-auto max-w-xl space-y-2">
      <Section icon={Mail} title={t("social.invites")} count={invites.length} testId="social-invites">
        {invites.length === 0 ? (
          <p className="text-sm text-slate-400">{t("social.noInvites")}</p>
        ) : (
          <div className="space-y-2">
            {invites.map((inv) => (
              <InviteCard
                key={inv.id}
                invite={inv}
                onAnswer={onAnswerInvite}
                acting={inviteActing[inv.id]}
                error={inviteErrors[inv.id]}
                busy={busy}
              />
            ))}
          </div>
        )}
      </Section>
      <Section icon={Users} title={t("social.recent")} testId="social-recent">
        <p className="-mt-0.5 mb-2 text-[11px] text-slate-400">{t("social.inviteHint")}</p>
        <Recent onOpen={setCardOf} action={inviteButton} />
      </Section>
      <PlayerCardDialog userId={cardOf} onClose={() => setCardOf(null)} inviteBlocked={busy ? t("players.finishGame") : undefined} />
    </div>
  );
}

export default SocialTab;
